import { useEffect, useMemo, useState } from "react";
import SettingsIcon from "@mui/icons-material/Settings";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import CloseIcon from "@mui/icons-material/Close";
import ZoomOutMapIcon from "@mui/icons-material/ZoomOutMap";
import * as XLSX from "xlsx";
import {
	Alert,
	Autocomplete,
	Box,
	Button,
	CircularProgress,
	Dialog,
	IconButton,
	MenuItem,
	Pagination,
	Paper,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import AttrConfigDrawer from "./AttrConfigDrawer";
import { getFilterOptions, getOptionSearch, getSalesFilterOptions, getSizeContributionReport } from "./api";

/** Historical ACH scope: EBO / MBO / LFS only; optional store / state / region (dependent on channel). */
function buildSalesFiltersPayload(f) {
	if (!f) return null;
	const out = {};
	if (f.channel?.length) out.channel = f.channel.join(",");
	if (f.store?.length) out.store = f.store.join(",");
	if (f.state?.length) out.state = f.state.join(",");
	if (f.region?.length) out.region = f.region.join(",");
	return Object.keys(out).length ? out : null;
}

const SALES_CHANNEL_OPTIONS = ["EBO", "MBO", "LFS"];
const ATTRIBUTE_FILTER_FIELDS = [
	{ key: "color", label: "Color" },
	{ key: "palette_type", label: "Palette Type" },
	{ key: "fashionability", label: "Fashionability" },
	{ key: "occasion", label: "Occasion" },
	{ key: "sleeve", label: "Sleeve" },
	{ key: "sub_category", label: "Sub Category" },
	{ key: "sub_brand", label: "Sub Brand" },
	{ key: "winter_categorisation", label: "Winter Categorisation" },
	{ key: "gmt_length", label: "Garment Length" },
	{ key: "sleeve_length", label: "Sleeve Length" },
	{ key: "fit_type", label: "Fit Type" },
	{ key: "closure", label: "Closure" },
	{ key: "collar", label: "Collar" },
	{ key: "neck", label: "Neck" },
];

const emptySalesFilterSelection = {
	channel: [],
	store: [],
	state: [],
	region: [],
};

const emptyAttributeFilters = ATTRIBUTE_FILTER_FIELDS.reduce((acc, field) => {
	acc[field.key] = "";
	return acc;
}, {});

// ─── Image Lightbox ──────────────────────────────────────────────────────────
const ImageLightbox = ({ src, alt, open, onClose }) => {
	const [zoom, setZoom] = useState(1);
	const STEP = 0.25;
	const MIN = 0.5;
	const MAX = 4;

	useEffect(() => {
		if (open) setZoom(1);
	}, [open, src]);

	if (!src) return null;

	return (
		<Dialog open={open} onClose={onClose} maxWidth={false} PaperProps={{ sx: { background: "transparent", boxShadow: "none", overflow: "visible" } }}>
			<Box
				sx={{
					position: "relative",
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					background: "rgba(15,15,15,0.92)",
					borderRadius: 2,
					p: 1,
					minWidth: 320,
					minHeight: 200,
				}}>
				{/* Controls */}
				<Stack
					direction="row"
					alignItems="center"
					gap={0.5}
					sx={{
						position: "absolute",
						top: 8,
						right: 8,
						zIndex: 10,
						background: "rgba(0,0,0,0.55)",
						borderRadius: 2,
						px: 0.5,
						py: 0.3,
					}}>
					<Tooltip title="Zoom out"><span>
						<IconButton size="small" onClick={() => setZoom((z) => Math.max(MIN, +(z - STEP).toFixed(2)))} disabled={zoom <= MIN} sx={{ color: "#fff" }}>
							<ZoomOutIcon fontSize="small" />
						</IconButton>
					</span></Tooltip>
					<Typography sx={{ fontSize: "0.72rem", color: "#fff", minWidth: 36, textAlign: "center" }}>
						{Math.round(zoom * 100)}%
					</Typography>
					<Tooltip title="Zoom in"><span>
						<IconButton size="small" onClick={() => setZoom((z) => Math.min(MAX, +(z + STEP).toFixed(2)))} disabled={zoom >= MAX} sx={{ color: "#fff" }}>
							<ZoomInIcon fontSize="small" />
						</IconButton>
					</span></Tooltip>
					<Tooltip title="Reset zoom">
						<IconButton size="small" onClick={() => setZoom(1)} sx={{ color: "#fff" }}>
							<ZoomOutMapIcon fontSize="small" />
						</IconButton>
					</Tooltip>
					<Tooltip title="Close">
						<IconButton size="small" onClick={onClose} sx={{ color: "#fff", ml: 0.5 }}>
							<CloseIcon fontSize="small" />
						</IconButton>
					</Tooltip>
				</Stack>

				{/* Image */}
				<Box sx={{ overflow: "auto", maxWidth: "90vw", maxHeight: "85vh", mt: 4, borderRadius: 1 }}>
					<Box
						component="img"
						src={src}
						alt={alt}
						sx={{
							display: "block",
							transform: `scale(${zoom})`,
							transformOrigin: "top left",
							width: `${Math.round(zoom * 400)}px`,
							transition: "transform 0.15s ease",
						}}
					/>
				</Box>
			</Box>
		</Dialog>
	);
};

// Thumbnail that opens the lightbox on click
const ZoomableImage = ({ src, alt }) => {
	const [open, setOpen] = useState(false);
	if (!src) {
		return (
			<Box sx={{ width: 56, height: 72, display: "grid", placeItems: "center", bgcolor: "#f9fafb", color: "#9ca3af", fontSize: "0.6rem", borderRadius: 1, border: "1px solid #d1d5db" }}>
				No img
			</Box>
		);
	}
	return (
		<>
			<Box sx={{ position: "relative", display: "inline-block", cursor: "zoom-in" }} onClick={() => setOpen(true)}>
				<Box component="img" src={src} alt={alt} sx={{ width: 56, height: 72, objectFit: "cover", borderRadius: 1, border: "1px solid #d1d5db", display: "block" }} />
				<Box sx={{
					position: "absolute", inset: 0, borderRadius: 1,
					display: "flex", alignItems: "center", justifyContent: "center",
					opacity: 0, transition: "opacity 0.15s",
					bgcolor: "rgba(0,0,0,0.35)",
					"&:hover": { opacity: 1 },
				}}>
					<ZoomInIcon sx={{ color: "#fff", fontSize: "1.2rem" }} />
				</Box>
			</Box>
			<ImageLightbox src={src} alt={alt} open={open} onClose={() => setOpen(false)} />
		</>
	);
};

const getDefaultSeason = () => "AW-26";

const SIZE_ORDER = ["XXS", "XS", "S", "M", "L", "XL", "2XL", "3XL", "4XL", "5XL"];

/** Canonical token for sorting / donor checks (must match IMS outputs after normalize). */
function canonicalLetterSizeForSort(size) {
	if (size == null || size === "") return "";
	const u = String(size).trim().replace(/\s+/g, "").toUpperCase();
	return u === "XXL" ? "2XL" : u;
}

/** Column header: show 2XL even if legacy payload still says XXL. */
function displaySizeColumnLabel(size) {
	if (size == null || size === "") return "";
	const u = normalizeSizeLabel(size);
	return u === "XXL" ? "2XL" : String(size).trim();
}

function sortSizes(cols) {
	return [...cols].sort((a, b) => {
		const ia = SIZE_ORDER.indexOf(canonicalLetterSizeForSort(a));
		const ib = SIZE_ORDER.indexOf(canonicalLetterSizeForSort(b));
		if (ia !== -1 && ib !== -1) return ia - ib;
		if (ia !== -1) return -1;
		if (ib !== -1) return 1;
		const na = parseInt(a, 10);
		const nb = parseInt(b, 10);
		if (!isNaN(na) && !isNaN(nb)) return na - nb;
		return String(a).localeCompare(String(b));
	});
}

const REPORT_PRODUCT_GRID_BORDER = "2px solid #94a3b8";
/** Thick rule after Sub Brand — separates sticky product-attribute block from metric labels + seasonal grid */
const lastProductColDividerSx = { borderRight: REPORT_PRODUCT_GRID_BORDER };

const headerCellSx = {
	fontWeight: 700,
	fontSize: "0.68rem",
	whiteSpace: "nowrap",
	backgroundColor: "#f3f4f6",
	color: "#374151",
	borderColor: "#d1d5db",
	py: 0.65,
	px: 0.75,
};

const metricHeaderSx = {
	...headerCellSx,
	fontSize: "0.62rem",
	minWidth: 118,
	maxWidth: 140,
	width: 128,
	whiteSpace: "normal",
	lineHeight: 1.25,
	borderRight: "2px solid #cbd5e1",
	verticalAlign: "middle",
	boxSizing: "border-box",
};

/** rowspan’d product cells stack from the top (avoids “floating middle” beside 7 metric rows) */
const productRowSpanBodySx = { verticalAlign: "top", pt: 0.9 };

/**
 * Second sticky header row offset (px). Must clear row-1 height; avoid overlap with row-spanned
 * sticky cells (browsers paint rowspan + sticky poorly).
 */
const REPORT_TABLE_STICKY_SUB_TOP = 42;

const reportTableHeadRow1StickySx = {
	position: "sticky",
	top: 0,
	zIndex: 25,
	backgroundColor: "#f3f4f6",
	borderBottom: "1px solid #cbd5e1",
	isolation: "isolate",
};

const reportTableHeadRow2StickySx = {
	position: "sticky",
	top: REPORT_TABLE_STICKY_SUB_TOP,
	zIndex: 24,
	backgroundColor: "#e8eeff",
	borderBottom: "2px solid #94a3b8",
	isolation: "isolate",
};

/** Invisible spacer under row-1 product / metric labels (same column widths — no rowspan, no overlap). */
const reportTableHeadRow2LeftSpacerSx = {
	...headerCellSx,
	position: "sticky",
	top: REPORT_TABLE_STICKY_SUB_TOP,
	zIndex: 24,
	backgroundColor: "#f3f4f6",
	borderBottom: "2px solid #94a3b8",
	py: 0.35,
	px: 0.45,
	fontSize: "0.5rem",
	lineHeight: 0,
	color: "transparent",
	overflow: "hidden",
	userSelect: "none",
	pointerEvents: "none",
};

const bodyCellSx = {
	fontSize: "0.68rem",
	color: "#111827",
	borderColor: "#e5e7eb",
	py: 0.35,
	px: 0.65,
	verticalAlign: "middle",
};

const pctCellSx = {
	...bodyCellSx,
	color: "#6b7280",
	borderBottom: "none",
};

const splitCellSx = {
	...bodyCellSx,
	color: "#92400e",
	backgroundColor: "#fffbeb",
	fontWeight: 600,
	borderBottom: "1px solid #fcd34d",
};

const finalRatioCellSx = {
	...bodyCellSx,
	color: "#0f766e",
	backgroundColor: "#f0fdfa",
	fontWeight: 700,
	borderBottom: "1px solid #99f6e4",
};

/** MUI Outlined: same label position for empty + filled (avoids “Current Season” vs “Brand” mismatch). */
const filterOutlinedLabelProps = { shrink: true };
const compactFilterInputSx = {
	"& .MuiInputBase-root": {
		minHeight: 32,
		height: 32,
	},
	"& .MuiInputBase-input": {
		py: 0.55,
		fontSize: "0.72rem",
	},
	"& .MuiInputLabel-root": {
		fontSize: "0.72rem",
	},
};
const relookSelectFieldSx = { minWidth: 0, width: "100%", ...compactFilterInputSx };
const salesAutocompleteFieldSx = { minWidth: 140, maxWidth: 185, flex: "0 0 auto", ...compactFilterInputSx };

const measureLabelSx = {
	...bodyCellSx,
	fontSize: "0.6rem",
	fontWeight: 600,
	color: "#374151",
	backgroundColor: "#f3f4f6",
	minWidth: 108,
	maxWidth: 132,
	width: 124,
	whiteSpace: "normal",
	lineHeight: 1.25,
	borderRight: "2px solid #cbd5e1",
};

/** Compacted attribute cells (narrow headers + ellipsis in body; full text via native title) */
const metaAttrCompactSx = {
	...bodyCellSx,
	maxWidth: 76,
	px: 0.5,
	overflow: "hidden",
	textOverflow: "ellipsis",
	whiteSpace: "nowrap",
};

const subBrandCompactSx = {
	...bodyCellSx,
	maxWidth: 112,
	px: 0.5,
	overflow: "hidden",
	textOverflow: "ellipsis",
	whiteSpace: "nowrap",
};


const PREV_SEASON_COLORS = ["#1d4ed8", "#059669", "#b45309", "#7c3aed", "#be123c"];

/** Fixed buy pack for split row — same as Excel: `=ROUNDUP(share*8,0)` where share = contribution as decimal. */
const SPLIT_BUY_QTY = 8;
const SUGGESTED_RATIO_DONOR_SIZES = new Set(["3XL"]);

/** Excel anchor cell $J$2 — fixed at 8 (same as split pack). */
const FINAL_RATIO_J2 = 8;

/** Pivotal sizes for Final Ratio (~ Excel TRUE rows — S, M, L only). */
const PIVOTAL_SIZES_FINAL = new Set(["S", "M", "L"]);

function normalizeSizeLabel(size) {
	if (size == null || size === "") return "";
	return String(size).trim().replace(/\s+/g, "").toUpperCase();
}

/** Excel row ST%: B2/B3 as percent, two decimals. */
function formatSellThroughPct(soldQty, dispatchQty) {
	const b2 = Number(soldQty) || 0;
	const b3 = Number(dispatchQty) || 0;
	if (b3 > 0) return `${((100 * b2) / b3).toFixed(2)}%`;
	if (b2 <= 0) return "0.00%";
	return "100.00%";
}

/** Sold / dispatch contribution % from API (already 0–100 scale), two decimals. */
function formatContributionPct(contributionPct) {
	if (contributionPct == null || contributionPct === "") return "0.00%";
	const n = Number(contributionPct);
	if (!Number.isFinite(n)) return "0.00%";
	return `${n.toFixed(2)}%`;
}

function isPivotalSizeColumn(size) {
	return PIVOTAL_SIZES_FINAL.has(normalizeSizeLabel(size));
}

/** Tint for S/M/L columns (headers + per-size grid cells; Total column unstyled). */
const pivotalColumnHeaderBg = "#ccfbf1"; // teal-100, reads clearly vs #f0f4ff season band
const pivotalColumnBodyTint = "rgba(15, 118, 110, 0.07)";

/** Excel `B8`: ROUNDUP(IFS(...)); sell-through thresholds 20% / 85%. */
function finalRatioRoundedUnits(soldQty, dispatchQty, totalSoldQty, pivotal) {
	const b2 = Number(soldQty) || 0;
	const b3 = Number(dispatchQty) || 0;
	const h2 = Number(totalSoldQty) || 0;
	const j = FINAL_RATIO_J2;
	if (!(j > 0) || !(h2 > 0)) return 0;

	let b4;
	if (b3 > 0) b4 = b2 / b3;
	else if (b2 <= 0) b4 = 0;
	else b4 = 1;

	const b6 = b2 / h2;
	const LOW_ST = b4 < 0.2;
	const MID_BAND = b4 >= 0.2 && b4 < 0.85;
	const HIGH_ST = b4 >= 0.85;

	let raw = 0;
	if (!pivotal && LOW_ST) raw = 0;
	else if (pivotal && LOW_ST) raw = 1 / j;
	else if (!pivotal && MID_BAND) raw = b6 * j;
	else if (pivotal && MID_BAND) raw = b6 * j;
	else if (!pivotal && HIGH_ST) {
		const denom = h2 - b2 + (b2 / 0.85);
		raw = denom > 0 ? (b2 / 0.85) / denom * j : 0;
	} else if (pivotal && HIGH_ST) {
		const denom = h2 - b2 + (b2 / 0.85);
		raw = denom > 0 ? (b2 / 0.85) / denom * j : 0;
	}

	return Number.isFinite(raw) ? Math.ceil(raw) : 0;
}

/** Per season: size → Final Ratio integers for display columns `cols`. */
function buildFinalRatioMap(cols = [], sizes = [], dispatchSizes = [], totalSold, totalDispatchRaw) {
	const totalDispatch = Number(totalDispatchRaw) || 0;
	const salesQty = Object.fromEntries(sizes.map((s) => [s.size, Number(s.sold_qty) || 0]));
	const dispatchQty = Object.fromEntries(dispatchSizes.map((s) => [s.size, Number(s.sold_qty) || 0]));
	const canRun = Number(totalSold) > 0 && totalDispatch > 0;
	const out = {};
	cols.forEach((size) => {
		const key = normalizeSizeLabel(size);
		const pivotal = PIVOTAL_SIZES_FINAL.has(key);
		const b2 = salesQty[size] ?? 0;
		const b3 = dispatchQty[size] ?? 0;
		out[size] = canRun ? finalRatioRoundedUnits(b2, b3, totalSold, pivotal) : null;
	});
	return out;
}

function roundUpSplitFromPct(contributionPct, totalSold) {
	if (totalSold <= 0 || contributionPct == null) return 0;
	const share = Number(contributionPct) / 100;
	return Math.ceil(share * SPLIT_BUY_QTY);
}

function buildRatioSplitMap(sizeRows = [], totalSold, displaySizes = null) {
	const pctMap = Object.fromEntries(sizeRows.map((s) => [s.size, s.contribution_pct]));
	const sizeList = displaySizes?.length ? displaySizes : sizeRows.map((s) => s.size);
	return Object.fromEntries(sizeList.map((size) => [size, roundUpSplitFromPct(pctMap[size], totalSold)]));
}

function ratioIntFromFinalCell(v) {
	if (v == null) return 0;
	const n = Number(v);
	return Number.isFinite(n) ? Math.trunc(n) : 0;
}

/**
 * Suggested pack units: when `finalRatioMap` is passed (ST% + pivotal logic already ran),
 * start from those integers so the row total matches Final Ratio, zero donor sizes (e.g. 3XL),
 * then add donor units onto highest sold-qty receiver(s). Otherwise fall back to
 * ROUNDUP(Sold Contri % × 8) only (no dispatch → no Final map).
 */
function buildSuggestedRatioMap(sizeRows = [], totalSold, displaySizes = null, finalRatioMap = null) {
	const sizeList = displaySizes?.length ? displaySizes : sizeRows.map((s) => s.size);
	const pctMap = Object.fromEntries(sizeRows.map((s) => [s.size, Number(s.contribution_pct || 0)]));
	const qtyMap = Object.fromEntries(sizeRows.map((s) => [s.size, Number(s.sold_qty || 0)]));

	const useFinal =
		finalRatioMap != null && typeof finalRatioMap === "object" && Object.keys(finalRatioMap).length > 0;

	const suggestedMap = useFinal
		? Object.fromEntries(sizeList.map((size) => [size, ratioIntFromFinalCell(finalRatioMap[size])]))
		: buildRatioSplitMap(sizeRows, totalSold, sizeList);

	const donorSizes = sizeList.filter((size) => SUGGESTED_RATIO_DONOR_SIZES.has(size));
	const unitsToMove = donorSizes.reduce((sum, size) => sum + (suggestedMap[size] || 0), 0);

	if (totalSold <= 0 || unitsToMove <= 0) return suggestedMap;

	const receivers = sizeList.filter((size) => !SUGGESTED_RATIO_DONOR_SIZES.has(size) && (pctMap[size] || 0) > 0);

	if (!receivers.length) return suggestedMap;

	const stableIdx = (a, b) => sizeList.indexOf(a) - sizeList.indexOf(b);
	const maxSoldQty = Math.max(...receivers.map((size) => qtyMap[size] || 0), 0);
	let topReceivers;
	if (maxSoldQty > 0) {
		topReceivers = receivers.filter((size) => (qtyMap[size] || 0) === maxSoldQty).sort(stableIdx);
	} else {
		const maxPct = Math.max(...receivers.map((size) => pctMap[size] || 0), 0);
		topReceivers = receivers.filter((size) => (pctMap[size] || 0) === maxPct).sort(stableIdx);
	}

	donorSizes.forEach((size) => {
		suggestedMap[size] = 0;
	});
	for (let i = 0; i < unitsToMove; i += 1) {
		const receiver = topReceivers[i % topReceivers.length];
		suggestedMap[receiver] = (suggestedMap[receiver] || 0) + 1;
	}
	return suggestedMap;
}

function exportSizeContributionReport(report, fallbackSeason) {
	if (!report) return;
	const prevSeasons = report?.prev_seasons || [];
	const currentOptions = report?.current_options || [];
	const requestFilters = report?.request_filters || {};
	const selectedSeason = requestFilters?.current_season || fallbackSeason || "";

	const rows = [];
	currentOptions.forEach((opt) => {
		const displayPkey = buildDisplayPkey(opt);
		prevSeasons.forEach((season) => {
			const seasonData = opt?.prev_season_data?.[season] || {};
			const sizes = seasonData?.sizes || [];
			const dispatchSizes = seasonData?.dispatch_data?.sizes || [];
			const totalSold = seasonData?.total_sold ?? 0;
			const totalDispatch = seasonData?.dispatch_data?.total_dispatch ?? 0;
			const allSizes = sortSizes(Array.from(new Set([...sizes.map((s) => s.size), ...dispatchSizes.map((s) => s.size)])));
			const canFinalRatio = Number(totalSold) > 0 && Number(totalDispatch) > 0;
			const finalMapForSuggested = canFinalRatio ? buildFinalRatioMap(allSizes, sizes, dispatchSizes, totalSold, totalDispatch) : null;
			const suggestedRatioMap = buildSuggestedRatioMap(sizes, totalSold, allSizes, finalMapForSuggested);
			const salesMap = Object.fromEntries(sizes.map((s) => [s.size, s]));
			const dispatchMap = Object.fromEntries(dispatchSizes.map((s) => [s.size, s]));
			allSizes.forEach((size) => {
				const sizeRow = salesMap[size] || {};
				const dispatchRow = dispatchMap[size] || {};
				const contributionPct = Number(sizeRow?.contribution_pct ?? 0);
				const dispatchContriPct = Number(dispatchRow?.contribution_pct ?? 0);
				const b2 = Number(sizeRow?.sold_qty ?? 0);
				const b3 = Number(dispatchRow?.sold_qty ?? 0);
				const stPctNumeric =
					b3 > 0 ? Math.round(((100 * b2) / b3) * 100) / 100 : b2 > 0 ? 100 : 0;
				const pivotal = PIVOTAL_SIZES_FINAL.has(normalizeSizeLabel(size));
				const finalUnit = canFinalRatio ? finalRatioRoundedUnits(b2, b3, totalSold, pivotal) : null;
				rows.push({
					"Current Season": selectedSeason,
					"Previous Season": season,
					Option: opt.option_code || "",
					PKEY: displayPkey,
					Brand: opt.brand || "",
					Category: opt.category || "",
					"Sub Category": opt.sub_category || "",
					Color: opt.color || "",
					Palette: opt.palette_type || "",
					Fashionability: opt.fashionability || "",
					Occasion: opt.occasion || "",
					Sleeve: opt.sleeve || "",
					"Sub Brand": opt.sub_brand || "",
					Size: displaySizeColumnLabel(size) || size || "",
					"Historical Sold": b2,
					"Historical Dispatch": b3,
					"ST%": stPctNumeric,
					"Sold Contri %": Math.round(contributionPct * 100) / 100,
					"Disp. Contri %": Math.round(dispatchContriPct * 100) / 100,
					Final_Ratio: finalUnit ?? "",
					"Suggested Ratio": suggestedRatioMap[size] || 0,
					"Total Sold (Season)": Number(totalSold || 0),
					"Total Dispatch (Season)": Number(totalDispatch || 0),
				});
			});
		});
	});

	if (!rows.length) return;

	const worksheet = XLSX.utils.json_to_sheet(rows);
	const workbook = XLSX.utils.book_new();
	XLSX.utils.book_append_sheet(workbook, worksheet, "Size Contribution");

	const stamp = new Date().toISOString().slice(0, 10);
	const fileSeason = String(selectedSeason || "report").replace(/[^\w-]/g, "_");
	XLSX.writeFile(workbook, `size_contribution_${fileSeason}_${stamp}.xlsx`);
}

// Build display PKEY: BRAND-CATEGORY-STYLE-COLOR-PALETTE
function buildDisplayPkey(row) {
	const parts = (row.option_code || "").split("-");
	if (parts.length >= 2 && row.style) {
		return [parts[0], parts[1], row.style, ...parts.slice(2)].join("-");
	}
	return row.option_code || "";
}

function AbbrevHeaderTopCell({ fullLabel, shortLabel, sx = {} }) {
	return (
		<TableCell sx={{ ...headerCellSx, ...reportTableHeadRow1StickySx, ...sx }}>
			<Tooltip title={fullLabel} placement="bottom-start" arrow>
				<Box component="span" sx={{ cursor: "help", display: "inline-block", width: "100%" }}>
					{shortLabel}
				</Box>
			</Tooltip>
		</TableCell>
	);
}

function ReportTableLegend() {
	return (
		<Stack
			direction="row"
			flexWrap="wrap"
			useFlexGap
			sx={{
				gap: 1.25,
				mb: 1,
				py: 0.85,
				px: 1.25,
				borderRadius: 1,
				border: "1px solid #e5e7eb",
				backgroundColor: "#fafafa",
			}}>
			<Stack direction="row" alignItems="flex-start" spacing={0.75} sx={{ minWidth: { xs: "100%", sm: 240 }, maxWidth: 420 }}>
				<Box
					sx={{
						mt: "2px",
						width: 12,
						height: 12,
						borderRadius: "2px",
						bgcolor: pivotalColumnHeaderBg,
						flexShrink: 0,
						border: "1px solid rgba(13, 148, 136, 0.35)",
					}}
				/>
				<Typography variant="caption" sx={{ fontSize: "0.65rem", color: "#4b5563", lineHeight: 1.4 }}>
					<strong style={{ color: "#111827" }}>S / M / L</strong> — pivotal bands; tint matches Excel focus sizes.
				</Typography>
			</Stack>
			<Stack direction="row" alignItems="flex-start" spacing={0.75} sx={{ minWidth: { xs: "100%", sm: 220 }, maxWidth: 400 }}>
				<Box sx={{ mt: "2px", width: 12, height: 12, borderRadius: "2px", bgcolor: "#f0fdfa", flexShrink: 0, border: "1px solid #99f6e4" }} />
				<Typography variant="caption" sx={{ fontSize: "0.65rem", color: "#4b5563", lineHeight: 1.4 }}>
					<strong style={{ color: "#0f766e" }}>Final Ratio</strong> — rounded units from ST% rules (same pivotal logic).
				</Typography>
			</Stack>
			<Stack direction="row" alignItems="flex-start" spacing={0.75} sx={{ minWidth: { xs: "100%", sm: 260 }, maxWidth: 440 }}>
				<Box sx={{ mt: "2px", width: 12, height: 12, borderRadius: "2px", bgcolor: "#fffbeb", flexShrink: 0, border: "1px solid #fcd34d" }} />
				<Typography variant="caption" sx={{ fontSize: "0.65rem", color: "#4b5563", lineHeight: 1.4 }}>
					<strong style={{ color: "#92400e" }}>Suggested Ratio</strong> — matches Final Ratio total; 3XL units move to highest sold-qty size(s). Without dispatch data, uses ROUNDUP(Sold Contri % × 8) only.
				</Typography>
			</Stack>
		</Stack>
	);
}

// ─── Report Table ───────────────────────────────────────────────────────────
// Left: current season option (image, PKEY, category, sub cat)
// Right: for each prev season, aggregated size ratio columns
const ReportTable = ({ currentOptions = [], prevSeasons = [], currentSeason }) => {
	// Collect all unique sizes across all prev seasons across all options
	const sizesByPrevSeason = useMemo(() => {
		const map = {};
		prevSeasons.forEach((season) => {
			const seen = new Set();
			currentOptions.forEach((opt) => {
				const sizes = opt.prev_season_data?.[season]?.sizes || [];
				const dispatchSizes = opt.prev_season_data?.[season]?.dispatch_data?.sizes || [];
				sizes.forEach((s) => seen.add(s.size));
				dispatchSizes.forEach((s) => seen.add(s.size));
			});
			map[season] = sortSizes(Array.from(seen));
		});
		return map;
	}, [currentOptions, prevSeasons]);

	if (!currentOptions.length) {
		return (
			<Box sx={{ p: 2, border: "1px solid #e5e7eb", borderRadius: 1, mb: 2 }}>
				<Typography variant="body2" color="text.secondary">No options in the current season result.</Typography>
			</Box>
		);
	}

	const totalPrevCols = prevSeasons.reduce((acc, s) => acc + (sizesByPrevSeason[s]?.length || 0) + 1, 0); // +1 for Total per season

	return (
		<TableContainer
			sx={{
				border: "1px solid #d1d5db",
				borderRadius: 1,
				maxHeight: "min(75vh, 880px)",
				overflow: "auto",
				position: "relative",
				isolation: "isolate",
			}}>
			<Table
				size="small"
				sx={{
					minWidth: Math.max(900, 520 + totalPrevCols * 44),
					width: "max-content",
					borderCollapse: "collapse",
				}}>
				<TableHead>
					{/* Row 1: product + metric labels; season banners (explicit row 2 for sizes avoids rowspan/sticky overlap) */}
					<TableRow>
						<TableCell
							sx={{ ...headerCellSx, ...reportTableHeadRow1StickySx, width: 40, maxWidth: 44, px: 0.45, textAlign: "center" }}>
							#
						</TableCell>
						<TableCell sx={{ ...headerCellSx, ...reportTableHeadRow1StickySx, width: 70, maxWidth: 74, px: 0.35 }}>URL</TableCell>
						<TableCell
							sx={{
								...headerCellSx,
								...reportTableHeadRow1StickySx,
								maxWidth: 176,
								minWidth: 136,
								whiteSpace: "normal",
								lineHeight: 1.2,
							}}>
							PKEY
						</TableCell>
						<TableCell sx={{ ...headerCellSx, ...reportTableHeadRow1StickySx, maxWidth: 78, px: 0.45 }}>Category</TableCell>
						<TableCell sx={{ ...headerCellSx, ...reportTableHeadRow1StickySx, maxWidth: 72, px: 0.45 }}>Sub Cat</TableCell>
						<TableCell sx={{ ...headerCellSx, ...reportTableHeadRow1StickySx, maxWidth: 74, px: 0.45 }}>Color</TableCell>
						<TableCell sx={{ ...headerCellSx, ...reportTableHeadRow1StickySx, maxWidth: 72, px: 0.45 }}>Palette</TableCell>
						<AbbrevHeaderTopCell fullLabel="Fashionability" shortLabel="Fash." sx={{ maxWidth: 72, px: 0.45 }} />
						<AbbrevHeaderTopCell fullLabel="Occasion" shortLabel="Occas." sx={{ maxWidth: 68, px: 0.45 }} />
						<AbbrevHeaderTopCell fullLabel="Sleeve" shortLabel="Sleeve" sx={{ maxWidth: 64, px: 0.45 }} />
						<TableCell sx={{ ...headerCellSx, ...reportTableHeadRow1StickySx, ...lastProductColDividerSx, maxWidth: 114, px: 0.45 }}>
							Sub Brand
						</TableCell>
						<TableCell sx={{ ...metricHeaderSx, ...reportTableHeadRow1StickySx }}>
							<Typography sx={{ fontSize: "0.62rem", fontWeight: 800, lineHeight: 1.25 }}>Metric</Typography>
						</TableCell>
						{prevSeasons.map((season, si) => {
							const cols = sizesByPrevSeason[season] || [];
							const color = PREV_SEASON_COLORS[si % PREV_SEASON_COLORS.length];
							return (
								<TableCell
									key={`grp-${season}`}
									colSpan={cols.length + 1}
									sx={{
										...headerCellSx,
										...reportTableHeadRow1StickySx,
										borderLeft: "2px solid #94a3b8",
										borderBottom: "1px solid #c7d2fe",
										textAlign: "center",
										color,
										background: "linear-gradient(180deg, #eef2ff 0%, #e0e7ff 100%)",
										zIndex: 21,
										fontWeight: 800,
										letterSpacing: "0.02em",
										fontSize: "0.72rem",
										verticalAlign: "middle",
									}}>
									{season}
								</TableCell>
							);
						})}
					</TableRow>
					<TableRow>
						<TableCell sx={{ ...reportTableHeadRow2LeftSpacerSx, width: 40, maxWidth: 44, textAlign: "center" }}>{/* r2 */}</TableCell>
						<TableCell sx={{ ...reportTableHeadRow2LeftSpacerSx, width: 70, maxWidth: 74, px: 0.35 }} />
						<TableCell sx={{ ...reportTableHeadRow2LeftSpacerSx, maxWidth: 176, minWidth: 136 }} />
						<TableCell sx={{ ...reportTableHeadRow2LeftSpacerSx, maxWidth: 78, px: 0.45 }} />
						<TableCell sx={{ ...reportTableHeadRow2LeftSpacerSx, maxWidth: 72, px: 0.45 }} />
						<TableCell sx={{ ...reportTableHeadRow2LeftSpacerSx, maxWidth: 74, px: 0.45 }} />
						<TableCell sx={{ ...reportTableHeadRow2LeftSpacerSx, maxWidth: 72, px: 0.45 }} />
						<TableCell sx={{ ...reportTableHeadRow2LeftSpacerSx, maxWidth: 72, px: 0.45 }} />
						<TableCell sx={{ ...reportTableHeadRow2LeftSpacerSx, maxWidth: 68, px: 0.45 }} />
						<TableCell sx={{ ...reportTableHeadRow2LeftSpacerSx, maxWidth: 64, px: 0.45 }} />
						<TableCell sx={{ ...reportTableHeadRow2LeftSpacerSx, ...lastProductColDividerSx, maxWidth: 114, px: 0.45 }} />
						<TableCell
							sx={{
								...reportTableHeadRow2LeftSpacerSx,
								minWidth: 118,
								maxWidth: 140,
								width: 128,
								borderRight: "2px solid #cbd5e1",
								fontWeight: 400,
							}}
						/>
						{prevSeasons.map((season, si) => {
							const cols = sizesByPrevSeason[season] || [];
							const color = PREV_SEASON_COLORS[si % PREV_SEASON_COLORS.length];
							return [
								...cols.map((size, ci) => {
									const pivotalCol = isPivotalSizeColumn(size);
									return (
										<TableCell
											key={`sz-${season}-${size}`}
											title={pivotalCol ? "Pivotal size (S, M, L)" : undefined}
											sx={{
												...headerCellSx,
												...reportTableHeadRow2StickySx,
												textAlign: "center",
												color,
												backgroundColor: pivotalCol ? pivotalColumnHeaderBg : "#e8eeff",
												borderLeft: ci === 0 ? "2px solid #94a3b8" : undefined,
												zIndex: 23,
												fontWeight: pivotalCol ? 800 : 600,
											}}>
											{displaySizeColumnLabel(size)}
										</TableCell>
									);
								}),
								<TableCell
									key={`total-${season}`}
									sx={{
										...headerCellSx,
										...reportTableHeadRow2StickySx,
										textAlign: "center",
										color,
										backgroundColor: "#e8eeff",
										fontStyle: "italic",
										zIndex: 23,
									}}>
									Total
								</TableCell>,
							];
						})}
					</TableRow>
				</TableHead>

				<TableBody>
					{currentOptions.map((opt, idx) => {
						const rowId = opt.report_row_key || `${opt.option_code}-${idx}`;
						const displayPkey = buildDisplayPkey(opt);
						const reportBodyRowSpan = 7;
						return [
							/* 1 — Historical Sold (Excel row 2) */
							<TableRow key={`sold-${rowId}`}>
								<TableCell
									sx={{
										...bodyCellSx,
										...productRowSpanBodySx,
										borderBottom: "none",
										width: 40,
										textAlign: "center",
										fontWeight: 700,
									}}
									rowSpan={reportBodyRowSpan}>
									{idx + 1}
								</TableCell>
								<TableCell sx={{ ...bodyCellSx, ...productRowSpanBodySx, borderBottom: "none", width: 72, px: 0.35 }} rowSpan={reportBodyRowSpan}>
									<ZoomableImage src={opt.image_url} alt={displayPkey} />
								</TableCell>
								<TableCell
									sx={{ ...bodyCellSx, ...productRowSpanBodySx, borderBottom: "none", maxWidth: 176, minWidth: 136, verticalAlign: "top" }}
									rowSpan={reportBodyRowSpan}>
									<Tooltip
										title={
											<Box>
												<div><b>PKEY:</b> {displayPkey}</div>
												{opt.attrs_used?.length ? (
													<div style={{ marginTop: 4 }}><b>Matched on:</b> {opt.attrs_used.join(", ")}</div>
												) : null}
												{opt.attrs_used?.map((a) => (
													<div key={a} style={{ fontSize: "0.7rem" }}>• {a.replace(/_/g, " ")}: {opt[a] || "—"}</div>
												))}
											</Box>
										}
										placement="right"
										arrow>
										<Typography
											sx={{
												fontSize: "0.68rem",
												fontWeight: 600,
												lineHeight: 1.35,
												overflow: "hidden",
												display: "-webkit-box",
												WebkitLineClamp: 2,
												WebkitBoxOrient: "vertical",
												wordBreak: "break-word",
												cursor: "help",
											}}>
											{displayPkey}
										</Typography>
									</Tooltip>
								</TableCell>
								<TableCell
									title={(opt.category && String(opt.category)) || undefined}
									sx={{ ...metaAttrCompactSx, ...productRowSpanBodySx, borderBottom: "none" }}
									rowSpan={reportBodyRowSpan}>
									{opt.category || "-"}
								</TableCell>
								<TableCell
									title={(opt.sub_category && String(opt.sub_category)) || undefined}
									sx={{ ...metaAttrCompactSx, ...productRowSpanBodySx, borderBottom: "none" }}
									rowSpan={reportBodyRowSpan}>
									{opt.sub_category || "-"}
								</TableCell>
								<TableCell
									title={(opt.color && String(opt.color)) || undefined}
									sx={{ ...metaAttrCompactSx, ...productRowSpanBodySx, borderBottom: "none" }}
									rowSpan={reportBodyRowSpan}>
									{opt.color || "-"}
								</TableCell>
								<TableCell
									title={(opt.palette_type && String(opt.palette_type)) || undefined}
									sx={{ ...metaAttrCompactSx, ...productRowSpanBodySx, borderBottom: "none" }}
									rowSpan={reportBodyRowSpan}>
									{opt.palette_type || "-"}
								</TableCell>
								<TableCell
									title={(opt.fashionability && String(opt.fashionability)) || undefined}
									sx={{ ...metaAttrCompactSx, ...productRowSpanBodySx, borderBottom: "none" }}
									rowSpan={reportBodyRowSpan}>
									{opt.fashionability || "-"}
								</TableCell>
								<TableCell
									title={(opt.occasion && String(opt.occasion)) || undefined}
									sx={{ ...metaAttrCompactSx, ...productRowSpanBodySx, borderBottom: "none" }}
									rowSpan={reportBodyRowSpan}>
									{opt.occasion || "-"}
								</TableCell>
								<TableCell
									title={(opt.sleeve && String(opt.sleeve)) || undefined}
									sx={{ ...metaAttrCompactSx, ...productRowSpanBodySx, borderBottom: "none" }}
									rowSpan={reportBodyRowSpan}>
									{opt.sleeve || "-"}
								</TableCell>
								<TableCell
									title={(opt.sub_brand && String(opt.sub_brand)) || undefined}
									sx={{ ...subBrandCompactSx, ...productRowSpanBodySx, borderBottom: "none", ...lastProductColDividerSx }}
									rowSpan={reportBodyRowSpan}>
									{opt.sub_brand || "-"}
								</TableCell>
								<TableCell sx={{ ...measureLabelSx, borderBottom: "none" }}>Historical Sold</TableCell>
								{prevSeasons.map((season, si) => {
									const cols = sizesByPrevSeason[season] || [];
									const sizes = opt.prev_season_data?.[season]?.sizes || [];
									const sizeMap = Object.fromEntries(sizes.map((s) => [s.size, s.sold_qty]));
									const total = opt.prev_season_data?.[season]?.total_sold ?? 0;
									return [
										...cols.map((size, ci) => (
											<TableCell
												key={`sold-${rowId}-${season}-${size}`}
												sx={{
													...bodyCellSx,
													borderBottom: "none",
													textAlign: "center",
													borderLeft: ci === 0 ? "2px solid #94a3b8" : undefined,
													...(isPivotalSizeColumn(size) ? { backgroundColor: pivotalColumnBodyTint } : {}),
												}}>
												{sizeMap[size] ?? 0}
											</TableCell>
										)),
										<TableCell
											key={`total-sold-${rowId}-${season}`}
											sx={{ ...bodyCellSx, borderBottom: "none", textAlign: "center", fontWeight: 600 }}>
											{total.toLocaleString()}
										</TableCell>,
									];
								})}
							</TableRow>,

							/* 2 — Historical Dispatch (Excel row 3) */
							<TableRow key={`dispatch-${rowId}`}>
								<TableCell sx={{ ...measureLabelSx, borderBottom: "none" }}>Historical Dispatch</TableCell>
								{prevSeasons.map((season, si) => {
									const cols = sizesByPrevSeason[season] || [];
									const dispatchData = opt.prev_season_data?.[season]?.dispatch_data || {};
									const dispatchSizes = dispatchData?.sizes || [];
									const dispatchMap = Object.fromEntries(dispatchSizes.map((s) => [s.size, s.sold_qty]));
									const totalDispatch = dispatchData?.total_dispatch ?? 0;
									return [
										...cols.map((size, ci) => (
											<TableCell
												key={`dispatch-${rowId}-${season}-${size}`}
												sx={{
													...bodyCellSx,
													borderBottom: "none",
													textAlign: "center",
													borderLeft: ci === 0 ? "2px solid #94a3b8" : undefined,
													...(isPivotalSizeColumn(size) ? { backgroundColor: pivotalColumnBodyTint } : {}),
												}}>
												{dispatchMap[size] ?? 0}
											</TableCell>
										)),
										<TableCell
											key={`total-dispatch-${rowId}-${season}`}
											sx={{ ...bodyCellSx, borderBottom: "none", textAlign: "center", fontWeight: 600 }}>
											{Number(totalDispatch || 0).toLocaleString()}
										</TableCell>,
									];
								})}
							</TableRow>,

							/* 3 — ST% = Sold / Dispatch (B4=B2/B3) */
							<TableRow key={`st-${rowId}`}>
								<TableCell sx={{ ...measureLabelSx, color: "#6b7280", borderBottom: "none" }}>ST%</TableCell>
								{prevSeasons.map((season, si) => {
									const cols = sizesByPrevSeason[season] || [];
									const sizes = opt.prev_season_data?.[season]?.sizes || [];
									const dispatchData = opt.prev_season_data?.[season]?.dispatch_data || {};
									const dispatchSizes = dispatchData?.sizes || [];
									const sizeMap = Object.fromEntries(sizes.map((s) => [s.size, s.sold_qty]));
									const dispatchMap = Object.fromEntries(dispatchSizes.map((s) => [s.size, s.sold_qty]));
									const totalSold = opt.prev_season_data?.[season]?.total_sold ?? 0;
									const totalDispatch = dispatchData?.total_dispatch ?? 0;
									return [
										...cols.map((size, ci) => (
											<TableCell
												key={`st-${rowId}-${season}-${size}`}
												sx={{
													...pctCellSx,
													textAlign: "center",
													borderLeft: ci === 0 ? "2px solid #94a3b8" : undefined,
													...(isPivotalSizeColumn(size) ? { backgroundColor: pivotalColumnBodyTint } : {}),
												}}>
												{formatSellThroughPct(sizeMap[size] ?? 0, dispatchMap[size] ?? 0)}
											</TableCell>
										)),
										<TableCell
											key={`total-st-${rowId}-${season}`}
											sx={{ ...pctCellSx, textAlign: "center", fontStyle: "italic", fontSize: "0.62rem" }}>
											{totalDispatch > 0 ? formatSellThroughPct(totalSold, totalDispatch) : "—"}
										</TableCell>,
									];
								})}
							</TableRow>,

							/* 4 — Sold Contri % */
							<TableRow key={`sold-contri-${rowId}`}>
								<TableCell sx={{ ...measureLabelSx, color: "#6b7280", borderBottom: "none" }}>Sold Contri %</TableCell>
								{prevSeasons.map((season, si) => {
									const cols = sizesByPrevSeason[season] || [];
									const sizes = opt.prev_season_data?.[season]?.sizes || [];
									const totalSold = opt.prev_season_data?.[season]?.total_sold ?? 0;
									const pctMap = Object.fromEntries(sizes.map((s) => [s.size, s.contribution_pct]));
									return [
										...cols.map((size, ci) => (
											<TableCell
												key={`sold-contri-${rowId}-${season}-${size}`}
												sx={{
													...pctCellSx,
													textAlign: "center",
													borderLeft: ci === 0 ? "2px solid #94a3b8" : undefined,
													...(isPivotalSizeColumn(size) ? { backgroundColor: pivotalColumnBodyTint } : {}),
												}}>
												{formatContributionPct(pctMap[size])}
											</TableCell>
										)),
										<TableCell
											key={`total-sold-contri-${rowId}-${season}`}
											sx={{ ...pctCellSx, textAlign: "center", fontStyle: "italic", fontSize: "0.62rem" }}>
											{totalSold > 0 ? "100.00%" : "—"}
										</TableCell>,
									];
								})}
							</TableRow>,

							/* 5 — Disp. Contri % */
							<TableRow key={`disp-contri-${rowId}`}>
								<TableCell sx={{ ...measureLabelSx, color: "#6b7280", borderBottom: "none" }}>Disp. Contri %</TableCell>
								{prevSeasons.map((season, si) => {
									const cols = sizesByPrevSeason[season] || [];
									const dispatchData = opt.prev_season_data?.[season]?.dispatch_data || {};
									const dispatchSizes = dispatchData?.sizes || [];
									const totalDispatch = dispatchData?.total_dispatch ?? 0;
									const dispatchPctMap = Object.fromEntries(dispatchSizes.map((s) => [s.size, s.contribution_pct]));
									return [
										...cols.map((size, ci) => (
											<TableCell
												key={`disp-contri-${rowId}-${season}-${size}`}
												sx={{
													...pctCellSx,
													textAlign: "center",
													borderLeft: ci === 0 ? "2px solid #94a3b8" : undefined,
													...(isPivotalSizeColumn(size) ? { backgroundColor: pivotalColumnBodyTint } : {}),
												}}>
												{formatContributionPct(dispatchPctMap[size])}
											</TableCell>
										)),
										<TableCell
											key={`total-disp-contri-${rowId}-${season}`}
											sx={{ ...pctCellSx, textAlign: "center", fontStyle: "italic", fontSize: "0.62rem" }}>
											{totalDispatch > 0 ? "100.00%" : "—"}
										</TableCell>,
									];
								})}
							</TableRow>,

							/* 6 — Final Ratio */
							<TableRow key={`final-ratio-${rowId}`}>
								<TableCell sx={{ ...measureLabelSx, borderBottom: "none" }}>Final Ratio</TableCell>
								{prevSeasons.map((season, si) => {
									const cols = sizesByPrevSeason[season] || [];
									const sizes = opt.prev_season_data?.[season]?.sizes || [];
									const dispatchData = opt.prev_season_data?.[season]?.dispatch_data || {};
									const dispatchSizes = dispatchData?.sizes || [];
									const totalSold = opt.prev_season_data?.[season]?.total_sold ?? 0;
									const totalDispatch = dispatchData?.total_dispatch ?? 0;
									const show = totalSold > 0 && totalDispatch > 0;
									const finalMap = buildFinalRatioMap(cols, sizes, dispatchSizes, totalSold, totalDispatch);
									const finalSum = cols.reduce((acc, size) => acc + (show ? (finalMap[size] ?? 0) : 0), 0);
									return [
										...cols.map((size, ci) => (
											<TableCell
												key={`final-ratio-${rowId}-${season}-${size}`}
												sx={{
													...finalRatioCellSx,
													textAlign: "center",
													borderLeft: ci === 0 ? "2px solid #94a3b8" : undefined,
													...(isPivotalSizeColumn(size) ? { backgroundColor: pivotalColumnBodyTint } : {}),
												}}>
												{show ? (finalMap[size] ?? 0) : "—"}
											</TableCell>
										)),
										<TableCell
											key={`total-final-ratio-${rowId}-${season}`}
											sx={{ ...finalRatioCellSx, textAlign: "center" }}>
											{show ? finalSum : "—"}
										</TableCell>,
									];
								})}
							</TableRow>,

							/* 7 — Suggested ratio */
							<TableRow key={`suggested-${rowId}`}>
								<TableCell sx={measureLabelSx}>Suggested Ratio</TableCell>
								{prevSeasons.map((season, si) => {
									const cols = sizesByPrevSeason[season] || [];
									const sizes = opt.prev_season_data?.[season]?.sizes || [];
									const dispatchData = opt.prev_season_data?.[season]?.dispatch_data || {};
									const dispatchSizes = dispatchData?.sizes || [];
									const totalSold = opt.prev_season_data?.[season]?.total_sold ?? 0;
									const totalDispatch = dispatchData?.total_dispatch ?? 0;
									const showSuggested = totalSold > 0 && totalDispatch > 0;
									const finalMapForSuggested = showSuggested
										? buildFinalRatioMap(cols, sizes, dispatchSizes, totalSold, totalDispatch)
										: null;
									const suggestedMap = buildSuggestedRatioMap(sizes, totalSold, cols, finalMapForSuggested);
									const suggestedSum = cols.reduce((acc, size) => acc + (suggestedMap[size] || 0), 0);
									return [
										...cols.map((size, ci) => (
											<TableCell
												key={`suggested-${rowId}-${season}-${size}`}
												sx={{
													...splitCellSx,
													textAlign: "center",
													borderLeft: ci === 0 ? "2px solid #94a3b8" : undefined,
													...(isPivotalSizeColumn(size) ? { backgroundColor: pivotalColumnBodyTint } : {}),
												}}>
												{totalSold > 0 ? suggestedMap[size] : "—"}
											</TableCell>
										)),
										<TableCell
											key={`total-suggested-${rowId}-${season}`}
											sx={{ ...splitCellSx, textAlign: "center" }}>
											{totalSold > 0 ? suggestedSum : "—"}
										</TableCell>,
									];
								})}
							</TableRow>,
						];
					})}
				</TableBody>
			</Table>
		</TableContainer>
	);
};

// ─── Browse Table (before report run) ───────────────────────────────────────
const BrowseTable = ({ rows = [], selectedOptionCode, onSelectOption }) => {
	if (!rows.length) {
		return (
			<Box sx={{ p: 2, border: "1px solid #e5e7eb", borderRadius: 1 }}>
				<Typography variant="body2" color="text.secondary">No options found for selected filters.</Typography>
			</Box>
		);
	}
	return (
		<TableContainer sx={{ border: "1px solid #d1d5db", borderRadius: 1 }}>
			<Table size="small" stickyHeader>
				<TableHead>
					<TableRow>
						<TableCell sx={headerCellSx}>#</TableCell>
						<TableCell sx={headerCellSx}>URL</TableCell>
						<TableCell sx={headerCellSx}>PKEY</TableCell>
						<TableCell sx={headerCellSx}>Season</TableCell>
						<TableCell sx={headerCellSx}>Category</TableCell>
						<TableCell sx={headerCellSx}>Sub Cat</TableCell>
						<TableCell sx={headerCellSx}>Color</TableCell>
						<TableCell sx={headerCellSx}>Palette</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{rows.map((row, idx) => {
						const selected = selectedOptionCode === row.option_code;
						const displayPkey = buildDisplayPkey(row);
						return (
							<TableRow
								key={`browse-${row.option_code}-${idx}`}
								onClick={() => onSelectOption?.(selected ? null : row)}
								sx={{ cursor: "pointer", backgroundColor: selected ? "rgba(25,118,210,0.1)" : "transparent", "&:hover": { backgroundColor: "#f9fafb" } }}>
								<TableCell sx={bodyCellSx}>{idx + 1}</TableCell>
								<TableCell sx={bodyCellSx}>
									<ZoomableImage src={row.image_url} alt={displayPkey} />
								</TableCell>
								<TableCell sx={{ ...bodyCellSx, maxWidth: 200 }}>
									<Tooltip title={displayPkey} placement="top">
										<Typography sx={{ fontSize: "0.68rem", fontWeight: 600, wordBreak: "break-word", whiteSpace: "normal" }}>{displayPkey}</Typography>
									</Tooltip>
								</TableCell>
								<TableCell sx={bodyCellSx}>{row.season || "-"}</TableCell>
								<TableCell sx={bodyCellSx}>{row.category || "-"}</TableCell>
								<TableCell sx={bodyCellSx}>{row.sub_category || "-"}</TableCell>
								<TableCell sx={bodyCellSx}>{row.color || "-"}</TableCell>
								<TableCell sx={bodyCellSx}>{row.palette_type || "-"}</TableCell>
							</TableRow>
						);
					})}
				</TableBody>
			</Table>
		</TableContainer>
	);
};

// ─── Main Component ──────────────────────────────────────────────────────────
const SizeContribution = () => {
	const [options, setOptions] = useState([]);
	const [selectedOption, setSelectedOption] = useState(null);
	const [loadingOptions, setLoadingOptions] = useState(false);
	const [loadingReport, setLoadingReport] = useState(false);
	const [error, setError] = useState("");
	const [report, setReport] = useState(null);
	const [drawerOpen, setDrawerOpen] = useState(false);
	const [currentSeason, setCurrentSeason] = useState(getDefaultSeason());
	const [prevSeasonCount, setPrevSeasonCount] = useState(1);
	const [seasonOptions, setSeasonOptions] = useState([]);
	const [brandOptions, setBrandOptions] = useState([]);
	const [categoryOptions, setCategoryOptions] = useState([]);
	const [brandFilter, setBrandFilter] = useState("RARE RABBIT");
	const [categoryFilter, setCategoryFilter] = useState("SHIRT");
	const [optionPage, setOptionPage] = useState(1);
	const optionPageSize = 20;
	const [salesOptions, setSalesOptions] = useState({
		stores: [],
		states: [],
		regions: [],
	});
	const [salesFilters, setSalesFilters] = useState(() => ({ ...emptySalesFilterSelection }));
	const [attributeFilters, setAttributeFilters] = useState(() => ({ ...emptyAttributeFilters }));

	useEffect(() => {
		let mounted = true;
		const load = async () => {
			try {
				const rows = await getFilterOptions();
				if (!mounted) return;
			const seasons = (rows?.season || []).map((r) => r?.key).filter(Boolean);
			setBrandOptions((rows?.brand || []).map((r) => r?.key).filter(Boolean));
			setCategoryOptions((rows?.category || []).map((r) => r?.key).filter(Boolean));
			setSeasonOptions(seasons);
			if (seasons.length) {
				setCurrentSeason((prev) => seasons.includes(prev) ? prev : seasons[0]);
			}
			} catch (_) {}
		};
		load();
		return () => { mounted = false; };
	}, []);

	/** Dependent store / state / region lists (BigQuery), scoped to EBO/MBO/LFS and parent selections. */
	useEffect(() => {
		let mounted = true;
		(async () => {
			try {
				const res = await getSalesFilterOptions({
					channel: salesFilters.channel,
					store: salesFilters.store,
					state: salesFilters.state,
				});
				if (!mounted) return;
				setSalesOptions({
					stores: res.stores || [],
					states: res.states || [],
					regions: res.regions || [],
				});
			} catch (err) {
				console.error("sales-filter-options", err?.response?.data || err);
				if (mounted) setSalesOptions({ stores: [], states: [], regions: [] });
			}
		})();
		return () => { mounted = false; };
	}, [salesFilters.channel, salesFilters.store, salesFilters.state]);

	useEffect(() => {
		let mounted = true;
		const timer = setTimeout(async () => {
			try {
				setLoadingOptions(true);
				const rows = await getOptionSearch({ brand: brandFilter, category: categoryFilter, season: currentSeason });
				if (!mounted) return;
				setOptions(rows);
				setAttributeFilters((prev) => {
					const next = { ...prev };
					ATTRIBUTE_FILTER_FIELDS.forEach(({ key }) => {
						const selected = String(prev[key] || "");
						if (!selected) return;
						const existsInRows = (rows || []).some((row) => String(row?.[key] || "") === selected);
						if (!existsInRows) next[key] = "";
					});
					return next;
				});
				setOptionPage(1);
				setReport(null);
			} catch (err) {
				if (!mounted) return;
				setError(err?.response?.data?.message || "Unable to load options");
			} finally {
				if (mounted) setLoadingOptions(false);
			}
		}, 300);
		return () => { mounted = false; clearTimeout(timer); };
	}, [brandFilter, categoryFilter, currentSeason]);

	const attributeFilterOptions = useMemo(() => {
		const out = {};
		ATTRIBUTE_FILTER_FIELDS.forEach(({ key }) => {
			const values = Array.from(
				new Set(
					options
						.map((row) => String(row?.[key] || "").trim())
						.filter(Boolean)
				)
			).sort((a, b) => a.localeCompare(b));
			out[key] = values;
		});
		return out;
	}, [options]);

	const filteredOptions = useMemo(() => (
		options.filter((row) => ATTRIBUTE_FILTER_FIELDS.every(({ key }) => {
			const selected = String(attributeFilters[key] || "");
			if (!selected) return true;
			return String(row?.[key] || "") === selected;
		}))
	), [options, attributeFilters]);

	useEffect(() => {
		if (!selectedOption) return;
		const stillVisible = filteredOptions.some((row) => row.option_code === selectedOption.option_code);
		if (!stillVisible) setSelectedOption(null);
	}, [filteredOptions, selectedOption]);

	const runReport = async () => {
		if (!filteredOptions.length) { setError("No options for selected filters"); return; }
		try {
			setLoadingReport(true);
			setError("");
			const payload = await getSizeContributionReport({
				optionCode: selectedOption?.option_code || "",
				optionCodes: selectedOption ? [selectedOption.option_code] : filteredOptions.map((r) => r.option_code),
				currentSeason,
				prevSeasonCount: Number(prevSeasonCount),
				filterBrand: brandFilter,
				filterCategory: categoryFilter,
				salesFilters: buildSalesFiltersPayload(salesFilters),
			});
			setReport(payload);
		} catch (err) {
			setError(err?.response?.data?.message || "Unable to fetch report");
		} finally {
			setLoadingReport(false);
		}
	};

	const paginatedOptions = useMemo(
		() => filteredOptions.slice((optionPage - 1) * optionPageSize, optionPage * optionPageSize),
		[filteredOptions, optionPage]
	);
	const optionPageCount = Math.max(1, Math.ceil(filteredOptions.length / optionPageSize));

	const prevSeasons = report?.prev_seasons || [];
	const currentOptions = report?.current_options || [];
	const activeAttrs = report?.config?.attributes || [];
	const reqFilters = report?.request_filters;
	const showBrand = reqFilters != null ? (reqFilters.filter_brand || "All") : (brandFilter || "All");
	const showCategory = reqFilters != null ? (reqFilters.filter_category || "All") : (categoryFilter || "All");
	const showSeason = reqFilters?.current_season || currentSeason;
	const showPrevN = reqFilters?.prev_season_count != null ? reqFilters.prev_season_count : prevSeasonCount;

	const salesScopeSummary = useMemo(() => {
		const sf = reqFilters?.sales_filters;
		if (!sf || typeof sf !== "object") return "";
		const parts = Object.entries(sf)
			.filter(([, v]) => v != null && String(v).trim() !== "")
			.map(([k, v]) => `${k.replace(/_/g, " ")}: ${v}`);
		return parts.join(" · ");
	}, [reqFilters]);

	return (
		<Box sx={{ p: 1 }}>
			{/* ST Pictorial–aligned sales drill-down (ALL_CHANNELS_SALES_DATA + PIN + SITE) — above Relook options */}
			<Paper sx={{ p: 1.5, borderRadius: 2, border: "1px solid #e5e7eb", boxShadow: "none", mb: 1.5 }}>
				<Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: "#374151", mb: 1 }}>
					Historical sales scope (optional — EBO, MBO, LFS; refine by store, state, region)
				</Typography>
				<Stack direction="row" flexWrap="wrap" gap={1} useFlexGap>
					<Autocomplete
						multiple
						size="small"
						options={SALES_CHANNEL_OPTIONS}
						value={salesFilters.channel}
						onChange={(_, v) => setSalesFilters((p) => ({ ...p, channel: v, store: [], state: [], region: [] }))}
						renderInput={(p) => (
							<TextField
								{...p}
								label="Channel"
								placeholder="All (EBO, MBO, LFS)"
								InputLabelProps={{ ...p.InputLabelProps, ...filterOutlinedLabelProps }}
							/>
						)}
						limitTags={1}
						sx={salesAutocompleteFieldSx}
					/>
					<Autocomplete
						multiple
						size="small"
						options={salesOptions.stores}
						value={salesFilters.store}
						onChange={(_, v) => setSalesFilters((p) => ({ ...p, store: v, state: [], region: [] }))}
						renderInput={(p) => (
							<TextField
								{...p}
								label="Store"
								placeholder="All"
								InputLabelProps={{ ...p.InputLabelProps, ...filterOutlinedLabelProps }}
							/>
						)}
						limitTags={1}
						sx={salesAutocompleteFieldSx}
					/>
					<Autocomplete
						multiple
						size="small"
						options={salesOptions.states}
						value={salesFilters.state}
						onChange={(_, v) => setSalesFilters((p) => ({ ...p, state: v, region: [] }))}
						renderInput={(p) => (
							<TextField
								{...p}
								label="State"
								placeholder="All"
								InputLabelProps={{ ...p.InputLabelProps, ...filterOutlinedLabelProps }}
							/>
						)}
						limitTags={1}
						sx={salesAutocompleteFieldSx}
					/>
					<Autocomplete
						multiple
						size="small"
						options={salesOptions.regions}
						value={salesFilters.region}
						onChange={(_, v) => setSalesFilters((p) => ({ ...p, region: v }))}
						renderInput={(p) => (
							<TextField
								{...p}
								label="Region (zone)"
								placeholder="All"
								InputLabelProps={{ ...p.InputLabelProps, ...filterOutlinedLabelProps }}
							/>
						)}
						limitTags={1}
						sx={salesAutocompleteFieldSx}
					/>
					<Button size="small" variant="text" onClick={() => setSalesFilters({ ...emptySalesFilterSelection })} sx={{ alignSelf: "center" }}>
						Clear sales filters
					</Button>
				</Stack>
			</Paper>

			{/* Relook filter bar — brand / season / category / run (one grid: labels all float same as ST Pictorial) */}
			<Paper sx={{ p: 1.5, borderRadius: 2, border: "1px solid #e5e7eb", boxShadow: "none", mb: 1.5 }}>
				<Box
					sx={{
						display: "grid",
						gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", md: "repeat(3, minmax(0, 1fr))", lg: "repeat(9, minmax(0, 1fr))" },
						gap: 1,
					}}>
					<TextField
						fullWidth
						size="small"
						select
						label="Brand Filter"
						value={brandFilter}
						onChange={(e) => setBrandFilter(e.target.value)}
						InputLabelProps={filterOutlinedLabelProps}
						SelectProps={{ displayEmpty: true, renderValue: (v) => (v == null || v === "" ? "All" : v) }}
						sx={relookSelectFieldSx}>
						<MenuItem value="">All</MenuItem>
						{brandOptions.map((b) => <MenuItem key={b} value={b}>{b}</MenuItem>)}
					</TextField>
					<TextField
						fullWidth
						size="small"
						select
						label="Current Season"
						value={currentSeason}
						onChange={(e) => setCurrentSeason(e.target.value)}
						InputLabelProps={filterOutlinedLabelProps}
						SelectProps={{ displayEmpty: true, renderValue: (v) => (v == null || v === "" ? "All" : v) }}
						sx={relookSelectFieldSx}>
						<MenuItem value="">All</MenuItem>
						{seasonOptions.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
					</TextField>
					<TextField
						fullWidth
						size="small"
						select
						label="Category Filter"
						value={categoryFilter}
						onChange={(e) => setCategoryFilter(e.target.value)}
						InputLabelProps={filterOutlinedLabelProps}
						SelectProps={{ displayEmpty: true, renderValue: (v) => (v == null || v === "" ? "All" : v) }}
						sx={relookSelectFieldSx}>
						<MenuItem value="">All</MenuItem>
						{categoryOptions.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
					</TextField>
					<TextField
						fullWidth
						size="small"
						select
						label="Previous Seasons"
						value={prevSeasonCount}
						onChange={(e) => setPrevSeasonCount(Number(e.target.value))}
						InputLabelProps={filterOutlinedLabelProps}
						sx={relookSelectFieldSx}>
						{[1, 2, 3, 4, 5].map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
					</TextField>
					{ATTRIBUTE_FILTER_FIELDS.map((field) => (
						<TextField
							key={field.key}
							fullWidth
							size="small"
							select
							label={field.label}
							value={attributeFilters[field.key]}
							onChange={(e) => setAttributeFilters((prev) => ({ ...prev, [field.key]: e.target.value }))}
							InputLabelProps={filterOutlinedLabelProps}
							SelectProps={{ displayEmpty: true, renderValue: (v) => (v == null || v === "" ? "All" : v) }}
							sx={relookSelectFieldSx}>
							<MenuItem value="">All</MenuItem>
							{(attributeFilterOptions[field.key] || []).map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
						</TextField>
					))}
				</Box>
				<Stack direction="row" gap={1} alignItems="center" justifyContent="space-between" flexWrap="wrap" sx={{ mt: 1 }}>
					<Button size="small" variant="text" onClick={() => setAttributeFilters({ ...emptyAttributeFilters })}>
						Clear attribute filters
					</Button>
					<Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
						<Button
							variant="contained"
							onClick={runReport}
							disabled={loadingReport || loadingOptions || !filteredOptions.length}
							sx={{ height: 34 }}>
							{loadingReport ? "Running..." : "Run Report"}
						</Button>
						{report && (
							<Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => setReport(null)} sx={{ height: 34 }}>
								Clear
							</Button>
						)}
						{report && (
							<Button
								variant="outlined"
								startIcon={<DownloadIcon />}
								onClick={() => exportSizeContributionReport(report, currentSeason)}
								sx={{ height: 34 }}>
								Download Report
							</Button>
						)}
						<Tooltip title="Configure matching attributes">
							<IconButton onClick={() => setDrawerOpen(true)} size="small">
								<SettingsIcon fontSize="small" />
							</IconButton>
						</Tooltip>
					</Stack>
				</Stack>
			</Paper>

			{/* Report summary strip */}
			{report && (
				<Paper sx={{ px: 2, py: 1, mb: 1.5, border: "1px solid #e5e7eb", boxShadow: "none", borderRadius: 2, background: "#f9fafb" }}>
					<Stack direction="row" gap={4} flexWrap="wrap" alignItems="flex-start">
						<Box><Typography sx={{ fontSize: "0.62rem", color: "#6b7280", fontWeight: 700 }}>BRAND FILTER</Typography><Typography sx={{ fontSize: "0.82rem" }}>{showBrand || "All"}</Typography></Box>
						<Box><Typography sx={{ fontSize: "0.62rem", color: "#6b7280", fontWeight: 700 }}>CURRENT SEASON</Typography><Typography sx={{ fontSize: "0.82rem", fontWeight: 700 }}>{showSeason}</Typography></Box>
						<Box><Typography sx={{ fontSize: "0.62rem", color: "#6b7280", fontWeight: 700 }}>CATEGORY FILTER</Typography><Typography sx={{ fontSize: "0.82rem" }}>{showCategory || "All"}</Typography></Box>
						<Box><Typography sx={{ fontSize: "0.62rem", color: "#6b7280", fontWeight: 700 }}>PREVIOUS SEASONS</Typography><Typography sx={{ fontSize: "0.82rem" }}>{prevSeasons.join(", ") || showPrevN}</Typography></Box>
						<Box>
							<Typography sx={{ fontSize: "0.62rem", color: "#6b7280", fontWeight: 700 }}>PURPOSE</Typography>
							<Typography sx={{ fontSize: "0.8rem", color: "#059669", fontWeight: 600 }}>
								Size ratio suggestion for {showSeason} buy
							</Typography>
						</Box>
						{activeAttrs.length > 0 && (
							<Box sx={{ flex: 1, minWidth: 220 }}>
								<Typography sx={{ fontSize: "0.62rem", color: "#6b7280", fontWeight: 700 }}>MATCHING ATTRIBUTES (from MongoDB)</Typography>
								<Typography sx={{ fontSize: "0.75rem" }}>{activeAttrs.map((a) => a.replace(/_/g, " ").toUpperCase()).join(", ")}</Typography>
							</Box>
						)}
						{salesScopeSummary ? (
							<Box sx={{ flex: 1, minWidth: 220 }}>
								<Typography sx={{ fontSize: "0.62rem", color: "#6b7280", fontWeight: 700 }}>HISTORICAL SALES SCOPE (BQ)</Typography>
								<Typography sx={{ fontSize: "0.72rem" }}>{salesScopeSummary}</Typography>
							</Box>
						) : null}
					</Stack>
				</Paper>
			)}

			{error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}

			{loadingReport && <Stack alignItems="center" py={6}><CircularProgress /></Stack>}

			{/* Browse table — before report run */}
			{!report && !loadingReport && (
				<Box>
					<Typography sx={{ fontSize: "0.85rem", fontWeight: 700, mb: 0.8 }}>
						{currentSeason} Options ({filteredOptions.length}){loadingOptions ? " — loading..." : ""}
						{selectedOption ? ` — Selected: ${buildDisplayPkey(selectedOption)}` : " — click a row to select, or Run Report on all"}
					</Typography>
					<BrowseTable rows={paginatedOptions} selectedOptionCode={selectedOption?.option_code} onSelectOption={setSelectedOption} />
					{filteredOptions.length > optionPageSize && (
						<Stack alignItems="center" mt={1.5}>
							<Pagination color="primary" page={optionPage} count={optionPageCount} onChange={(_, v) => setOptionPage(v)} />
						</Stack>
					)}
				</Box>
			)}

			{/* Report table */}
			{!loadingReport && report && (
				<Box>
					<Typography sx={{ fontSize: "0.85rem", fontWeight: 700, mb: 0.35 }}>
						{currentSeason} Options ({currentOptions.length}) — with suggested size ratios from {prevSeasons.join(", ")}
					</Typography>
					<ReportTableLegend />
					<ReportTable currentOptions={currentOptions} prevSeasons={prevSeasons} currentSeason={currentSeason} />
				</Box>
			)}

			<AttrConfigDrawer
				open={drawerOpen}
				onClose={() => setDrawerOpen(false)}
				onSaved={() => {}}
				filterBrand={brandFilter}
				filterCategory={categoryFilter}
			/>
		</Box>
	);
};

export default SizeContribution;
